import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { PackageCheck, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import StorePinGate from "@/components/StorePinGate";

const fmt = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

function BatchSummary({ b }) {
  return (
    <div className="rounded-lg bg-secondary/50 p-3 text-sm space-y-0.5" data-testid="delivery-batch-summary">
      <p className="font-semibold">{b.label || b.store_name}</p>
      <p className="text-muted-foreground">Concorso: {b.contest_name}</p>
      <p className="text-muted-foreground">Destinato a: <b className="text-foreground">{b.store_name}</b> · {b.quantity} codici{b.prefix ? ` · prefisso ${b.prefix}` : ""}</p>
    </div>
  );
}

function ConfirmForm({ batch, token, store, onDone }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.confirmBatchDelivery(batch.id, token, name);
      toast.success("Ricezione confermata");
      onDone(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Conferma non riuscita");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4" data-testid="delivery-confirm-form">
      <p className="text-sm text-emerald-400">Sede autenticata: <b>{store.name}</b></p>
      <div className="space-y-1.5">
        <Label>Nome e cognome di chi riceve il lotto</Label>
        <Input data-testid="delivery-receiver-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Maria Rossi" />
      </div>
      <Button data-testid="delivery-confirm-button" onClick={submit} disabled={busy || name.trim().length < 2} className="w-full h-12 gap-2">
        <PackageCheck className="h-4 w-4" /> {busy ? "Salvataggio…" : "Confermo la ricezione del lotto"}
      </Button>
      <p className="text-[11px] text-muted-foreground">La conferma con data, ora, sede e nome viene registrata come verbale digitale di consegna.</p>
    </div>
  );
}

export default function BatchDelivery() {
  const { batchId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [batch, setBatch] = useState(null);
  const [error, setError] = useState(null);
  const [store, setStore] = useState(null);

  useEffect(() => {
    api.batchDelivery(batchId, token).then(setBatch).catch((e) => setError(e?.response?.data?.detail || "QR non valido"));
    api.storeMe().then(setStore).catch(() => {});
  }, [batchId, token]);

  let body;
  if (error) body = <p className="text-red-400" data-testid="delivery-error">{error}</p>;
  else if (!batch) body = <p className="text-muted-foreground">Caricamento…</p>;
  else if (batch.delivered_at) {
    body = (
      <div className="text-center space-y-2 py-4" data-testid="delivery-done">
        <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-400" />
        <p className="font-semibold text-lg">Ricezione già confermata</p>
        <p className="text-sm text-muted-foreground">{fmt(batch.delivered_at)} · {batch.delivered_store_name} · ricevuto da {batch.delivered_by}</p>
      </div>
    );
  } else if (!store) body = <StorePinGate embedded onUnlock={setStore} />;
  else body = <ConfirmForm batch={batch} token={token} store={store} onDone={(r) => setBatch({ ...batch, ...r })} />;

  return (
    <div className="min-h-screen bg-background grid place-items-center p-4">
      <Card className="w-full max-w-md p-6 bg-card border-border space-y-4" data-testid="batch-delivery-page">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-primary/15 grid place-items-center"><PackageCheck className="h-6 w-6 text-primary" /></div>
          <div>
            <h1 className="font-display font-bold text-lg">Verbale di consegna lotto</h1>
            <p className="text-xs text-muted-foreground">Conferma digitale della ricezione dei codici</p>
          </div>
        </div>
        {batch && <BatchSummary b={batch} />}
        {body}
      </Card>
    </div>
  );
}
